import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUsers, FiFileText, FiBrain, FiShield, FiArrowRight, FiCheck } = FiIcons;

const HomePage = ({ onAuthRequired }) => {
  const navigate = useNavigate();

  const agreementTypes = [
    {
      id: 'partnership',
      title: 'Co-Founder Partnership',
      description: 'Create fair equity agreements for startup founders and business partners',
      icon: FiUsers,
      color: 'from-blue-500 to-blue-600',
      features: ['Equity splits', 'Vesting schedules', 'Exit clauses', 'IP ownership']
    },
    {
      id: 'contractor',
      title: 'Independent Contractor',
      description: 'Professional service agreements for freelancers and contractors',
      icon: FiFileText,
      color: 'from-purple-500 to-purple-600',
      features: ['Scope of work', 'Payment terms', 'IP rights', 'Confidentiality']
    },
    {
      id: 'employment',
      title: 'Employee Agreement',
      description: 'Comprehensive employment contracts and offer letters',
      icon: FiShield,
      color: 'from-emerald-500 to-emerald-600',
      features: ['Salary & benefits', 'Job responsibilities', 'At-will terms', 'Non-compete']
    }
  ];

  const features = [
    {
      icon: FiBrain,
      title: 'AI-Powered Guidance',
      description: 'Get intelligent suggestions for fair terms and potential risks'
    },
    {
      icon: FiUsers,
      title: 'Real-Time Collaboration',
      description: 'Work together with multiple parties in live editing sessions'
    },
    {
      icon: FiFileText,
      title: 'Professional Documents',
      description: 'Generate legally sound contracts ready for signing'
    },
    {
      icon: FiShield,
      title: 'Secure & Private',
      description: 'Bank-level encryption protects your sensitive information'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-8 gradient-text">
              Create Fair Agreements <br />
              <span className="text-slate-800">Together</span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-600 mb-12 max-w-4xl mx-auto leading-relaxed">
              AI-powered collaborative platform for drafting customized business agreements. 
              No more endless emails or one-sided contracts.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16"
          >
            <button
              onClick={() => navigate('/wizard/partnership')}
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold text-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
            >
              Start Creating
              <SafeIcon icon={FiArrowRight} />
            </button>
            <button className="px-8 py-4 border-2 border-slate-300 text-slate-700 rounded-xl font-semibold text-lg hover:bg-slate-50 transition-all duration-200">
              Watch Demo
            </button>
          </motion.div>
        </div>
      </section>

      {/* Agreement Types */}
      <section className="py-16 px-6 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-slate-800 mb-6">
              Choose Your Agreement Type
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Our AI guides you through the right questions for your specific situation
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {agreementTypes.map((type, index) => (
              <motion.div
                key={type.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                onClick={() => navigate(`/wizard/${type.id}`)}
                className="glass-effect rounded-2xl p-8 cursor-pointer hover:shadow-glow transform hover:scale-105 transition-all duration-300 group"
              >
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-r ${type.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-200`}>
                  <SafeIcon icon={type.icon} className="text-2xl text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 mb-4">{type.title}</h3>
                <p className="text-slate-600 mb-6 leading-relaxed">{type.description}</p>
                <ul className="space-y-2">
                  {type.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-slate-700">
                      <SafeIcon icon={FiCheck} className="text-emerald-500 text-sm" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-slate-800 mb-6">
              Why Choose CoAgree AI?
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Built for fairness, powered by AI, designed for collaboration
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 flex items-center justify-center mx-auto mb-6 animate-float" style={{ animationDelay: `${index * 0.5}s` }}>
                  <SafeIcon icon={feature.icon} className="text-3xl text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800 mb-4">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
              Ready to Create Your Agreement?
            </h2>
            <p className="text-xl text-blue-100 mb-12 max-w-2xl mx-auto">
              Join thousands of entrepreneurs, freelancers, and businesses who trust CoAgree AI for their legal agreements.
            </p>
            <button
              onClick={() => navigate('/wizard/partnership')}
              className="px-10 py-5 bg-white text-blue-600 rounded-xl font-bold text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              Get Started Free
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;